import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTimeBoxedDailyAttention } from './store/useTimeBoxedDailyAttention';

const IDLE_MS = 8_000;

/**
 * Orquesta el AttentionPulse + Coachmark del Centro de ayuda.
 *
 * Reglas implementadas:
 *  - El pulso vive mientras dure la ventana de `windowDays` días desde `anchorDate`
 *    (ver useTimeBoxedDailyAttention): se repite cada día, un clic solo lo apaga por hoy.
 *  - El Coachmark se dispara por el primer evento que ocurra: cambio de módulo (ruta)
 *    o inactividad. El que dispara primero cancela al otro (nunca aparecen los dos).
 *  - Un clic directo en el botón marca el día como visto sin pasar por el coachmark.
 *  - La señal es por usuario (userId debe venir de la sesión, no por navegador/empresa).
 */
export function useHelpCenterCoachmark(userId: string | null | undefined, anchorDate: string | null, windowDays: number) {
  const key = `help_center:${userId ?? 'anonimo'}`;
  const { active, markSeenToday } = useTimeBoxedDailyAttention(key, anchorDate, windowDays);
  const [coachmarkOpen, setCoachmarkOpen] = useState(false);

  const location = useLocation();
  const firstPathRef = useRef<string | null>(null);
  const firedRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Disparador 1: primer cambio de módulo
  useEffect(() => {
    if (!active || firedRef.current) return;

    if (firstPathRef.current === null) {
      firstPathRef.current = location.pathname;
      return;
    }
    if (location.pathname !== firstPathRef.current) {
      firedRef.current = true;
      clearTimeout(idleTimerRef.current);
      setCoachmarkOpen(true);
    }
  }, [location.pathname, active]);

  // Disparador 2: inactividad (respaldo si no cambia de módulo)
  useEffect(() => {
    if (!active) return;
    idleTimerRef.current = setTimeout(() => {
      if (!firedRef.current) {
        firedRef.current = true;
        setCoachmarkOpen(true);
      }
    }, IDLE_MS);
    return () => clearTimeout(idleTimerRef.current);
  }, [active]);

  function dismissCoachmark() {
    setCoachmarkOpen(false);
    markSeenToday();
  }

  /** Clic afuera del cartelito: lo cierra sin marcarlo como visto (el halo sigue activo). */
  function closeCoachmark() {
    setCoachmarkOpen(false);
  }

  /** Llamar desde el onClick real del botón de ayuda */
  function handleDirectClick() {
    clearTimeout(idleTimerRef.current);
    firedRef.current = true;
    setCoachmarkOpen(false);
    markSeenToday();
  }

  return {
    pulseActive: active,
    coachmarkOpen,
    dismissCoachmark,
    closeCoachmark,
    handleDirectClick,
  };
}
